// pages/admin/compound/CompoundPage.js

import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WarningAmberRounded,
  Close,
  PersonAddAlt,
  ManageAccounts,
  Phone,
  AccountBalance,
  Person,
} from "@mui/icons-material";
import Header from "../components/Header";
import CompoundFormDialog from "../components/Compound/CompoundFormDialog";
import stringToColor from "../services/stringToColor";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "axios";
import { useUser } from "../provider/AuthProvider"; // Adjust the import path as necessary
import ManagerSettingDialog from "../components/Compound/ManagerSettingDialog";
import CMTAssignDialog from "../components/Compound/CMTAssignDialog";

const CompoundPage = () => {
  const { user } = useUser();
  const [compounds, setCompounds] = useState([]);
  const [cmts, setCmts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [managerDialogOpen, setManagerDialogOpen] = useState(false);
  const [cmtAssignDialogOpen, setCmtAssignDialogOpen] = useState(false);
  const [selectedCompound, setSelectedCompound] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'inactive'
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // or "error"
  const [isSubmit, setIsSubmit] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [assignedCMTs, setAssignedCMTs] = useState([]);
  const [availableCMTs, setAvailableCMTs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionPlan, setSubscriptionPlans] = useState([]);

  const handleConfirmOpen = (id) => {
    setSelectedId(id);
    setOpenConfirm(true);
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
    setSelectedId(null);
  };

  useEffect(() => {
    fetchCompoundList();
    fetchCMTs();
    fetchSubscriptionPlans();
  }, []);

  const filteredCompounds = useMemo(() => {
    return compounds.filter((compound) => {
      // Search by name
      const matchesSearch = compound.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      // Filter by active/inactive status
      const matchesFilter =
        statusFilter === "all" || compound.status === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [compounds, searchQuery, statusFilter]);

  const fetchCompoundList = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getCompoundList`,
        { withCredentials: true }
      );
      setCompounds(res.data.compounds);
    } catch (err) {
      console.error("Failed to fetch compounds", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCMTs = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getCMT`,
        { withCredentials: true }
      );
      console.log(res.data.data);
      setCmts(res.data.data);
    } catch (err) {
      console.error("Failed to fetch CMTs", err);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscription-plans`,
        { withCredentials: true }
      );
      console.log(res.data.data);
      setSubscriptionPlans(res.data.data);
    } catch (err) {
      console.error("Failed to fetch CMTs", err);
    }
  };
  const fetchAssignedCMTs = async (compoundId) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getAssignedCMT/${compoundId}`,
        { withCredentials: true }
      );
      console.log(res.data.data);
      setAssignedCMTs(res.data.data);
    } catch (err) {
      console.error("Failed to fetch CMTs", err);
    }
  };
  const fetchAvailableCMTs = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getAvailableCMT`,
        { withCredentials: true }
      );
      console.log(res.data.data);
      setAvailableCMTs(res.data.data);
    } catch (err) {
      console.error("Failed to fetch CMTs", err);
    }
  };

  const handleDeleteConfirmed = async (id) => {
    try {
      setIsSubmit(true);
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/compound/delete/${id}`,
        { withCredentials: true }
      );
      fetchCompoundList();
      fetchCMTs();
      handleConfirmClose();
      setSnackbarMessage("Compound has deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsSubmit(false);
    } catch (err) {
      console.error("Delete failed", err);
      setSnackbarMessage("Failed to delete compound!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsSubmit(false);
    }
  };
  const columns = [
    {
      field: "logo_url",
      headerName: "Logo", // Google Translate will translate this header text
      flex: 0.5,
      renderCell: (params) =>
        params?.row.logo_url ? (
          <Avatar
            alt={params?.row.name}
            src={params?.row.logo_url}
            className="notranslate"
          />
        ) : (
          <Avatar
            sx={{ bgcolor: stringToColor(params?.row?.name) }}
            className="notranslate"
          >
            {params?.row.name.charAt(0).toUpperCase()}
          </Avatar>
        ),
    },
    {
      field: "name",
      headerName: "Name", // this header text is translatable
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "address",
      headerName: "Address", // translatable header
      flex: 1.5,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "uniLimit",
      headerName: "UnitLimit",
      flex: 1,
      renderCell: (params) => (
        <Typography className="notranslate" color="yellow">
          {" "}
          {params?.row?.subscription_plan_name} (
          {params?.row?.subscription_plan_unitLimit})
        </Typography>
      ),
    },
    // For counts and statuses, you can also wrap row text in notranslate:
    {
      field: "tenants_count",
      headerName: "Tenants",
      flex: 0.5,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "landlords_count",
      headerName: "Landlords",
      flex: 0.5,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "service_providers_count",
      headerName: "Providers",
      flex: 0.5,
      renderCell: (params) => (
        <Typography className="notranslate">{params.value}</Typography>
      ),
    },
    {
      field: "units_count",
      headerName: "Units",
      flex: 0.5,
      renderCell: (params) => (
        <Typography
          className="notranslate"
          color={
            params?.row?.subscription_plan_unitLimit > params.value
              ? "normal"
              : "error"
          }
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params?.row?.status === "active" ? "success" : "error"}
        />
      ),
    },
    {
      field: "manager information",
      headerName: "Manager Information",
      flex: 1.5,
      renderCell: (params) => {
        if (!params?.row?.manager_id) {
          return (
            <Chip
              label="Not Created"
              className="notranslate"
              color="error"
              size="small"
            />
          );
        }
        return (
          <Box
            display="flex"
            flexDirection="column"
            py={1}
            gap={0.5}
            className="notranslate"
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <Person fontSize="small" />
              <Typography variant="body2">
                {params.row.manager_username}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <EmailIcon fontSize="small" />
              <Typography variant="body2">
                {params.row.manager_email}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Phone fontSize="small" />
              <Typography variant="body2" dir="ltr">
                {params.row.manager_phone}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 0.7,
      renderCell: (params) => (
        <Typography className="notranslate">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              onClick={() => {
                setSelectedCompound(params.row);
                setDialogOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Manager Settings">
            <IconButton
              onClick={() => {
                setSelectedCompound(params.row);
                setSelectedManager({
                  id: params.row.manager_id,
                  username: params.row.manager_username,
                  email: params.row.manager_email,
                  phone: params.row.manager_phone,
                });
                setManagerDialogOpen(true);
              }}
              color="success"
            >
              <ManageAccounts />
            </IconButton>
          </Tooltip>
          <Tooltip title="Assign CMT">
            <IconButton
              onClick={() => {
                setSelectedCompound(params.row);
                fetchAssignedCMTs(params.row.id);
                fetchAvailableCMTs();
                setCmtAssignDialogOpen(true);
              }}
              color="info"
            >
              <PersonAddAlt />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              onClick={() => handleConfirmOpen(params.row.id)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];
  return (
    <Box className="main-container mt-4">
      <Header title="Compound Management" />
      <Grid
        container
        spacing={2}
        columns={{ xs: 12, sm: 12, md: 12 }}
        sx={{
          width: "100%",
        }}
      >
        {/* <Grid
          item
          size={{ xs: 12, sm: 3, md: 3 }}
          display="flex"
          alignItems="center"
        >
          <Grid
            container
            spacing={2}
            columns={{ xs: 12, sm: 12, md: 12 }}
            sx={{
              width: "100%",
            }}
          >
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Paper
                sx={{
                  padding: 2,
                  height: "30vh",
                }}
              >
                <Box p={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Compound List
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
              <Paper
                sx={{
                  padding: 2,
                  height: "50vh",
                }}
              >
                <Box p={2}>
                  <Typography variant="h6" fontWeight="bold">
                    Compound List
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid> */}
        <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="Search Compound"
                variant="outlined"
                size="large"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  style: { color: "#cbd5e1" },
                }}
                InputLabelProps={{
                  style: { color: "#90a4ae" },
                }}
              />

              <FormControl size="large" minWidth={150}>
                <InputLabel
                  sx={{
                    color: "#90a4ae", // Default color
                    "&:hover": {
                      color: "#00b4d8", // Hover color
                    },
                    "&.Mui-focused": {
                      color: "#00b4d8", // Focus color
                    },
                  }}
                >
                  Status
                </InputLabel>

                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedCompound(null);
                setDialogOpen(true);
              }}
            >
              Add Compound
            </Button>
          </Box>

          <Box style={{ height: "70vh", width: "100%" }}>
            <DataGrid
              rows={filteredCompounds}
              columns={columns}
              getRowId={(row) => row.id}
              pageSize={10}
              loading={isLoading}
              getRowHeight={() => "auto"}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 15, 20, 25]}
              sx={{
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  justifyContent: "left",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  justifyContent: "center",
                  textAlign: "center",
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <CompoundFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        compound={selectedCompound}
        cmts={cmts}
        plans={subscriptionPlan}
        onSuccess={() => {
          setDialogOpen(false);
          fetchCompoundList();
          fetchCMTs();
        }}
      />
      <ManagerSettingDialog
        open={managerDialogOpen}
        onClose={() => setManagerDialogOpen(false)}
        manager={selectedManager}
        compoundId={selectedCompound?.id}
        onSave={() => {
          fetchCompoundList();
          setManagerDialogOpen(false);
        }}
      />

      <CMTAssignDialog
        open={cmtAssignDialogOpen}
        onClose={() => setCmtAssignDialogOpen(false)}
        assigned={assignedCMTs}
        available={availableCMTs}
        compoundId={selectedCompound?.id}
        onSave={() => {
          fetchCompoundList();
          setCmtAssignDialogOpen(false);
        }}
      />
      <Dialog
        open={openConfirm}
        onClose={handleConfirmClose}
        fullWidth
        keepMounted
        sx={{
          borderRadius: 10,
          px: 2,
          py: 1,
          boxShadow: 10,
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRounded color="warning" />
          <Typography fontWeight="bold" variant="h6">
            Confirm Delete
          </Typography>
          <Box flexGrow={1} />
          <IconButton onClick={handleConfirmClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography variant="h7">
            Are you sure you want to delete this compound? <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleDeleteConfirmed(selectedId)}
            variant="text"
            sx={{
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.light",
                color: "white",
              },
            }}
          >
            {isSubmit == true ? "Deleting ..." : "Delete"}
          </Button>
          <Button onClick={handleConfirmClose} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompoundPage;
